/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React, { useCallback, useContext, useMemo } from 'react';
import { useEntryPointButtonTrigger } from '@jira/platform__entry-point-button-trigger/src/index.tsx';
import { JiraEntryPointContainer } from '@jira/platform__entry-points-container/src/index.tsx';
// eslint-disable-next-line jira/restricted/@atlassian+jira-entry-point
import { useEntryPoint } from '@jira/platform__entry-point/src/controllers/use-entry-point/index.tsx';
import { NAVIGATION_ITEM } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/action-subject-type.tsx';
import { SECTION_ITEM_STARRED } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/section-type.tsx';
import { NavItemsContext } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/controllers/context.tsx';
import { L1_MENU_ID } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/types.tsx';
import { SkeletonNavMenuItems } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/skeleton-nav-menu-items/main.tsx';
import { useFormattedMessageAndIcon } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/utils/use-formatted-message-and-icon/index.tsx';
import {
	FlyoutMenuItem,
	FlyoutMenuItemTrigger,
	FlyoutMenuItemContent,
} from '@atlaskit/side-nav-items/flyout-menu-item';
import StarUnstarredIcon from '@atlaskit/icon/core/star-unstarred';
import { fg } from '@atlassian/jira-feature-gating';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';

import { defineMessage } from '@atlassian/jira-intl';
import {
	VALID_FAVORITE_TYPES,
	PACKAGE_NAME,
	TEAM_NAME,
	FAVOURITE_QUERY_LIMIT,
} from '../common/constants.tsx';
import { sidebarStarredMenuEntryPoint } from './starred-menu/sidebarStarredMenuEntryPoint.entrypoint.tsx';

const starred = defineMessage({
	id: 'navigation-apps-sidebar-nav4-starred.starred',

	defaultMessage: 'Starred',
	description: 'Message for starred nav menu item in the sidebar',
});

const EMPTY_OBJECT = {};

export function StarredFlyoutMenuItem(): React.JSX.Element {
	const cloudId = useCloudId();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { moreMenuIds } = useContext(NavItemsContext);

	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		sidebarStarredMenuEntryPoint,
		useMemo(
			() => ({
				cloudId,
				filter: {
					types: VALID_FAVORITE_TYPES,
					sort: { order: 'DESC' as const },
				},
				count: FAVOURITE_QUERY_LIMIT,
			}),
			[cloudId],
		),
	);

	const triggerRef = useEntryPointButtonTrigger(entryPointActions);

	const { formattedMessage, icon } = useFormattedMessageAndIcon(starred, StarUnstarredIcon);

	const onOpenChangeHandler = useCallback(
		async (isOpen: boolean) => {
			if (isOpen) {
				const event = createAnalyticsEvent({
					action: 'clicked',
					actionSubject: NAVIGATION_ITEM,
				});
				const isInMore = moreMenuIds.includes(L1_MENU_ID.STARRED);
				fireUIAnalytics(event, SECTION_ITEM_STARRED, { isInMore });
			}
		},
		[createAnalyticsEvent, moreMenuIds],
	);

	return (
		<FlyoutMenuItem onOpenChange={onOpenChangeHandler}>
			<FlyoutMenuItemTrigger
				interactionName="nav4-sidebar-starred"
				ref={triggerRef}
				elemBefore={icon}
			>
				{formattedMessage}
			</FlyoutMenuItemTrigger>
			<FlyoutMenuItemContent
				maxHeight={fg('platform_dst_nav4_flyout_menu_slots_close_button') ? 460 : undefined}
			>
				<JiraEntryPointContainer
					// Data stream, will subscribe to load updates.
					entryPointReferenceSubject={entryPointReferenceSubject}
					// Required props for error JSErrorBoundary.
					id="nav4-sidebar-starred-default"
					packageName={PACKAGE_NAME}
					teamName={TEAM_NAME}
					// Optional props for error JSErrorBoundary.
					errorFallback="flag"
					// Fallback for `Suspense`.
					fallback={<SkeletonNavMenuItems isFlyout withHeading withSearch />}
					// Props for the root component.
					runtimeProps={EMPTY_OBJECT}
				/>
			</FlyoutMenuItemContent>
		</FlyoutMenuItem>
	);
}
